import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "", testId }) => (
  <span className="block overflow-hidden" data-testid={testId}>
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function HeroSection({ surfaceImage, onBegin }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "16%"]);
  const raysY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[108vh] flex-col justify-center overflow-hidden px-6 pb-28 pt-24 md:px-12"
    >
      <motion.div
        style={{ y: raysY }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div className="absolute -top-1/3 left-[8%] h-[150%] w-24 rotate-[16deg] bg-gradient-to-b from-white/60 via-white/15 to-transparent blur-2xl md:w-40" />
        <div className="absolute -top-1/3 left-[38%] h-[150%] w-16 rotate-[20deg] bg-gradient-to-b from-white/45 via-white/10 to-transparent blur-2xl md:w-28" />
        <div className="absolute -top-1/3 left-[72%] h-[150%] w-32 rotate-[12deg] bg-gradient-to-b from-white/35 via-white/10 to-transparent blur-3xl md:w-52" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 grid grid-cols-12 items-center gap-10"
      >
        <div className="col-span-12 lg:col-span-8">
          <h1>
            <MaskedLine delay={0.18} testId="hero-title">
              <span className="flex items-end gap-4 text-[#02252e] md:gap-6">
                <img
                  src="/okeanos-mark.png"
                  alt="Okeanos wave mark"
                  className="w-[clamp(2.8rem,6.5vw,6rem)] shrink-0"
                />
                <span className="font-brand text-[clamp(3.8rem,12.5vw,11.5rem)] leading-[0.95]">
                  Oκεαηοs
                </span>
              </span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.3} testId="hero-overline">
            <span className="mt-6 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.4em] text-[#06414d] md:text-[11px]">
              <span className="inline-block h-2 w-2 bg-[#f58220]" />
              Coastal Engineering (P) Ltd.
            </span>
          </MaskedLine>
          <MaskedLine
            delay={0.42}
            testId="hero-tagline"
            className="mt-8 font-editorial text-[clamp(1.5rem,3.6vw,2.8rem)] italic leading-tight text-[#053540]"
          >
            Achieving continuous coastal sustainability
          </MaskedLine>
          <MaskedLine
            delay={0.5}
            testId="hero-copy"
            className="mt-8 max-w-md text-sm leading-relaxed text-[#083843]/85 md:text-base"
          >
            A multi-disciplinary consultancy for coastal, marine and
            environmental challenges — measured, modelled and engineered from
            the surface to the seafloor.
          </MaskedLine>
        </div>

        <div className="relative col-span-4 hidden lg:block">
          <motion.div
            style={{ y: imgY }}
            className="group relative ml-auto w-full max-w-sm"
          >
            <div className="relative overflow-hidden" data-testid="hero-image-frame">
              <img
                src={surfaceImage}
                alt="Aerial view of ocean surface waves"
                className="aspect-[3/4] w-full scale-[1.12] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.18]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#C8F2F4]/50 via-transparent to-white/10" />
            </div>
          </motion.div>
        </div>
      </motion.div>

      <button
        type="button"
        onClick={onBegin}
        data-testid="scroll-cue"
        className="group absolute bottom-10 left-6 z-10 flex items-center gap-4 md:left-12"
      >
        <motion.span
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="text-[#06414d]"
        >
          <ChevronDown size={16} strokeWidth={1.5} />
        </motion.span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-[#06414d]/70">
          0m
        </span>
      </button>
    </section>
  );
}
